import React, { useState, useEffect } from 'react';
import PlayerCard from '../PlayerCard';
import { mockPlayers } from '../../data/carouselMockData';

const LiveEngagementSlide: React.FC = () => {
  const [visiblePlayers, setVisiblePlayers] = useState<number>(0);
  const maxPlayers = 12; // Show first 12 players

  useEffect(() => {
    // Animate players joining one by one
    const interval = setInterval(() => {
      setVisiblePlayers((prev) => {
        if (prev < maxPlayers) {
          return prev + 1;
        }
        return prev;
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Real-Time Class Engagement
          </h2>
          <p className="text-xl text-gray-600 mb-6">
            Watch your classroom come alive as students join instantly
          </p>
        </div>

        {/* Game PIN Display */}
        <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-8 mb-8 text-center shadow-2xl">
          <p className="text-white text-lg font-semibold mb-2">Game PIN</p>
          <div className="text-6xl md:text-7xl font-bold text-white tracking-wider">
            ABC123
          </div>
          <div className="mt-4 flex items-center justify-center gap-2">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <p className="text-white text-lg font-medium">
              {visiblePlayers} of {mockPlayers.length} students connected
            </p>
          </div>
        </div>

        {/* Player Grid */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Students in Lobby</h3>
            {visiblePlayers === mockPlayers.length && (
              <div className="flex items-center gap-2 text-green-600 font-semibold animate-bounce">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Everyone's ready!</span>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {mockPlayers.slice(0, Math.min(visiblePlayers, maxPlayers)).map((player, index) => (
              <div
                key={player.id}
                className="transform transition-all duration-300"
                style={{
                  animation: 'slideUp 0.4s ease-out',
                  animationDelay: `${index * 50}ms`,
                  animationFillMode: 'both'
                }}
              >
                <PlayerCard player={{ ...player, score: 0 }} />
              </div>
            ))}
          </div>
          {visiblePlayers < mockPlayers.length && (
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                <span>More students joining...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default LiveEngagementSlide;
